/**
 * brioCAST Orders & Reconciliation Page
 * Drop-in for existing CRA portal
 *
 * Add to renderPage():
 *   case "orders": return hasCap(account, "FLASH_BUY") ? <OrdersPage account={account} /> : <NoCap cap="FLASH_BUY" />;
 * Add to Sidebar:
 *   { key: "orders", label: "訂單對帳", icon: "📋", cap: "FLASH_BUY" }
 */
import { useState, useEffect, useCallback } from "react";

const API_V1 = "https://api.briolabs.io/api/v1";
async function apiV1(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const res = await fetch(`${API_V1}${path}`, { ...options, headers });
  return res.json();
}

const t = {
  card: "#1A1D27", border: "#2A2E3A", accent: "#6C5CE7",
  success: "#00B894", danger: "#E17055", warning: "#FDCB6E",
  text: "#E8E8ED", textMuted: "#8B8FA3", textDim: "#5A5E72",
};

export default function OrdersPage({ account }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [recon, setRecon] = useState(null);
  const limit = 15;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFilter) params.set("status", statusFilter);
      const res = await apiV1(`/payments/transactions?${params}`);
      setOrders(res.data?.transactions || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch {} finally { setLoading(false); }
  }, [limit, page, statusFilter]);

  const fetchRecon = useCallback(async () => {
    try {
      const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Taipei" });
      const res = await apiV1(`/payments/reconciliation/daily?date=${today}`);
      setRecon(res.data?.reconciliation || null);
    } catch {}
  }, []);

  useEffect(() => { fetchOrders(); fetchRecon(); }, [fetchOrders, fetchRecon]);

  const totalPages = Math.ceil(total / limit);

  const statusColors = { PAID: t.success, PENDING: t.warning, FAILED: t.danger, REFUNDED: t.textMuted };
  const statusLabels = { PAID: "已完成", PENDING: "待付款", FAILED: "失敗", REFUNDED: "已退款" };

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>📋 訂單與對帳</h2>
      <p style={{ color: t.textMuted, fontSize: 14, marginBottom: 24 }}>ECPay 交易記錄與每日對帳明細</p>

      {/* Daily reconciliation card */}
      {recon && (
        <div style={{ background: t.card, borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", padding: 20, marginBottom: 16, display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 4, width: "100%" }}>📅 今日對帳 ({recon.date || "today"})</div>
          {[
            { label: "訂單數", value: recon.total_orders, color: t.text },
            { label: "已付款", value: `NT$${(recon.paid_amount || 0).toLocaleString()}`, color: t.success },
            { label: "平台手續費", value: `NT$${(recon.platform_fee || 0).toLocaleString()}`, color: t.warning },
            { label: "淨收入", value: `NT$${(recon.net_amount || 0).toLocaleString()}`, color: t.accent },
          ].map((item) => (
            <div key={item.label}>
              <div style={{ fontSize: 12, color: t.textDim }}>{item.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {["", "PAID", "PENDING", "FAILED"].map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{
            padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 8,
            border: `1px solid ${statusFilter === s ? `${t.accent}60` : t.border}`,
            background: statusFilter === s ? `${t.accent}15` : "transparent",
            color: statusFilter === s ? t.accentLight : t.textMuted,
            cursor: "pointer",
          }}>{s || "全部"}</button>
        ))}
      </div>

      {/* Orders table */}
      <div style={{ background: t.card, borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "130px 1fr 90px 90px 80px", padding: "10px 16px", borderBottom: `1px solid ${t.border}`, background: "rgba(0,0,0,0.2)" }}>
          {["訂單編號", "品項", "金額", "付款", "狀態"].map((h) => (
            <div key={h} style={{ fontSize: 11, fontWeight: 600, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>
            <div style={{ width: 20, height: 20, border: `2px solid ${t.border}`, borderTopColor: t.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
            載入中...
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: t.textDim }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>尚無交易記錄
          </div>
        ) : orders.map((o, i) => (
          <div key={o.id || i} style={{
            display: "grid", gridTemplateColumns: "130px 1fr 90px 90px 80px", padding: "11px 16px",
            alignItems: "center", borderBottom: i < orders.length - 1 ? `1px solid rgba(255,255,255,0.04)` : "none",
          }}>
            <div>
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>{(o.merchant_trade_no || "").slice(-8)}</div>
              <div style={{ fontSize: 10, color: t.textDim }}>{o.created_at ? new Date(o.created_at).toLocaleString("zh-TW", { timeZone: "Asia/Taipei", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</div>
            </div>
            <div style={{ fontSize: 13 }}>{o.item_name}</div>
            <div style={{ fontSize: 13, fontWeight: 600, fontFamily: "monospace" }}>NT$ {o.amount}</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>{o.payment_method || "ECPay"}</div>
            <div>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999,
                background: `${statusColors[o.status] || t.textDim}18`,
                color: statusColors[o.status] || t.textDim,
              }}>{statusLabels[o.status] || o.status}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 16 }}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} style={{ padding: "6px 12px", border: `1px solid ${t.border}`, borderRadius: 8, background: "transparent", color: page === 1 ? t.textDim : t.text, cursor: page === 1 ? "default" : "pointer" }}>‹</button>
          <span style={{ fontSize: 13, color: t.textMuted }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} style={{ padding: "6px 12px", border: `1px solid ${t.border}`, borderRadius: 8, background: "transparent", color: page === totalPages ? t.textDim : t.text, cursor: page === totalPages ? "default" : "pointer" }}>›</button>
        </div>
      )}
    </div>
  );
}
